<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：エリア登録マスタ画面
 *
 * 作成者：2021/11/22 ノア）黄
 * 最終修正者：2022/1/14 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.areaMaster.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('master.areaMaster.contentTitle') }}</span>
          <span>{{ tableData.length }}</span>
          <span class="viewTitle">{{ $t('master.viewTitle.subject') }}</span>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-form :model="form" ref="form" :disabled="pageLock">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 226px)"
          border
          highlight-current-row
          :stripe="true"
          header-row-class-name="tti-master-table-header"
          :empty-text="$t('messages.common.emptyTableData')"
          @selection-change="handleSelectionChange"
          :row-class-name="tableRowClassName"
          @sort-change="handleSortChange"
        >
          <el-table-column type="selection" width="35"></el-table-column>
          <!-- エリアコード -->
          <el-table-column
            class-name="required"
            prop="areaCode"
            :label="$t('master.areaMaster.areaCode')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].areaCode'" :rules="rules.areaCode">
                <el-input
                  :disabled="!canCodeChange(scope.row.dataType)"
                  v-model.trim="scope.row.areaCode"
                  @change="format(scope.row)"
                  size="small"
                  :id="'tableData[' + scope.$index + '].areaCode'"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- エリア名 -->
          <el-table-column
            class-name="required"
            prop="areaName"
            :label="$t('master.areaMaster.areaName')"
            width="500"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].areaName'" :rules="rules.areaName">
                <el-input
                  :disabled="!canCodeChange(scope.row.dataType)"
                  v-model.trim="scope.row.areaName"
                  @change="onRepeatItemChange(formRef, 'areaName')"
                  size="small"
                  :id="'tableData[' + scope.$index + '].areaName'"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onDeleteButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.delete)"
          ><tti-svg-icon icon-name="su_013"></tti-svg-icon>{{ $t('buttons.delete') }}</el-button
        >
        <!-- コピー -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onCopyButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.copy)"
        >
          <tti-svg-icon icon-name="su_012"></tti-svg-icon>{{ $t('buttons.copy') }}
        </el-button>
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onInsertButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.insert)"
        >
          <tti-svg-icon icon-name="su_011"></tti-svg-icon>{{ $t('buttons.insert') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'

import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'
import { i18n } from '@/locales'
import { getAreaMasterData, updateAreaMasterData } from '@/store/apis'
import { AreaMasterData, MasterData } from '@/models'
import { RULES, VALID_PATTERN } from '@/service/validateService'
import { msgBoxService } from '@/commons/services/msgBoxService'

/**
 * エリアマスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-area',
  components: {}
})
export default class MST_AREA extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'MST_AREA'

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'areaCode'

  /**
   * エリアマスタ編集FORM
   */
  form = {
    tableData: this.tableData
  } as Record<string, AreaMasterData[]>

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // エリアコード
      areaCode: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.areaMaster.areaCode')]).toString(),
          trigger: 'blur'
        },
        // 半角数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n.t('vaildate.common.numberWithLength', [i18n.t('master.areaMaster.areaCode'), 8]).toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 8文字
            {
              rule: RULES.RANGE,
              range: [8, 8],
              message: i18n.t('vaildate.common.numberWithLength', [i18n.t('master.areaMaster.areaCode'), 8]).toString()
            },
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              visableTableData: () => this.visableTableData,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              isDisabled: (row: any) => !this.canCodeChange(row.dataType),
              tableColumn: 'areaCode',
              message: i18n.t('vaildate.common.repeat', [i18n.t('master.areaMaster.areaCode')]).toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // エリア名
      areaName: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.areaMaster.areaName')]).toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 50文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.areaMaster.areaName'), 50]).toString()
            },
            // 重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              visableTableData: () => this.visableTableData,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              isDisabled: (row: any) => !this.canCodeChange(row.dataType),
              tableColumn: 'areaName',
              message: i18n.t('vaildate.common.repeat', [i18n.t('master.areaMaster.areaName')]).toString()
            }
          ],
          trigger: 'blur'
        }
      ]
    }
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('areaMaster > mounted')
    this.initFormTemplate()
    this.fetchTableData()
    this.checkPageLock()
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
    this.initSearchTargets()
  }

  protected fetchTableDataBefore(): () => Promise<MasterData> {
    return getAreaMasterData
  }

  /**
   * 8ビット未満自動補正0と重複チェックある項目を変更した後に実行する。
   *
   * @param {AreaMasterData} val エリアマスタ
   * @returns {void}
   */
  format(val: AreaMasterData): void {
    val.areaCode = this.prefixZero(val.areaCode)
    this.onRepeatItemChange(this.formRef, 'areaCode')
  }

  fetchTableDataAfter(): void {
    this.initRule()
    this.initValidate(this.rules)
    this.multipleSelection = []
  }

  /**
   * コピー前の処理。
   *
   * @param {Array<any>} multipleSelection コピー対象
   * @returns {void}
   */
  // eslint-disable-next-line
  beforeDataCopy(multipleSelection: Array<any>): void {
    multipleSelection.forEach(item => {
      delete item.subPrefecture
    })
  }

  /**
   * 削除前に、削除チェックを行う。
   *
   * @returns {boolean} チェック結果
   */
  deleteTableDataBefore(): boolean {
    let canDelete = true
    for (const item of this.multipleSelection) {
      if (item.subPrefecture) {
        msgBoxService.info(
          i18n
            .t('messages.masterCommon.cantDelete', [item.areaCode, i18n.t('master.areaMaster.subPrefecture')])
            .toString()
        )
        canDelete = false
        break
      }
      if (item.hasManagementPlace) {
        msgBoxService.info(
          i18n
            .t('messages.masterCommon.cantDelete', [item.areaCode, i18n.t('master.areaMaster.hasManagementPlace')])
            .toString()
        )
        canDelete = false
        break
      }
      if (item.hasUsedArea) {
        msgBoxService.info(
          i18n
            .t('messages.masterCommon.cantDelete', [item.areaCode, i18n.t('master.areaMaster.hasUsedArea')])
            .toString()
        )
        canDelete = false
        break
      }
    }

    return canDelete
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    return this.dataValidate()
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    const params = this.getUpsertRequest()
    await updateAreaMasterData(params)
  }

  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Input,
        field: 'areaCode'
      },
      {
        type: FormItemType.Input,
        field: 'areaName'
      }
    ]
  }

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {
    this.formTemplate = {
      areaCode: '',
      areaName: ''
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-viewTitle {
  margin-right: 20px;
}
</style>
